import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Form, Input, Space } from 'antd'
import { UploadFile } from 'antd/lib'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useStocksServiceCreateInternalInquiryEndpoint,
  useStocksServiceGetInternalInquiryEndpoint,
  useStocksServiceGetInternalInquiryEndpointKey,
  useStocksServiceSendInternalInquiryEndpoint,
  useStocksServiceUpdateInternalInquiryEndpoint
} from 'src/api/queries'
import { ApiError, InternalInventoryDocumentState } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CustomItemRender,
  LoadingScreen,
  message,
  PageBreadcrumbs,
  SaveConfirm,
  Upload
} from 'src/components'
import { useFormStyles } from 'src/features/stock/styles'
import {
  useFileHandler,
  useScrollToFirstError,
  ValidationError
} from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

import { LocalConsumableListCreate } from './LocalConsumableListCreate'

type MedicineProps = {
  inventoryItemId: string
  quantity: string
}

export const StockLocalApplicationsCreatePage = () => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const { styles } = useFormStyles()

  const [form] = Form.useForm()

  const { onScrollToError } = useScrollToFirstError(form)

  const { id } = useParams()
  const queryClient = useQueryClient()

  const {
    existingFiles,
    setExistingFiles,
    onFileChange,
    saveFiles,
    isUploading
  } = useFileHandler({
    form,
    folder: id ? `inquiries-${id}` : 'inquiries'
  })

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetInternalInquiryEndpointKey, String(id)]
    })
  }

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.stockLocal.applications)
  }

  const { mutate } = useStocksServiceCreateInternalInquiryEndpoint({
    onSuccess: (data) => {
      onMutationSuccess()
      navigate(paths.stockLocal.applicationEdit(data?.id))
      message.success(t('stock_applications.inquiry_created_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutateAsync: edit } = useStocksServiceUpdateInternalInquiryEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('common.save_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutateAsync: send } = useStocksServiceSendInternalInquiryEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('stock_applications.inquire_sent'))
      navigate(paths.stockLocal.applicationDetails(String(id)))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })
  const { data: inquiryData, isLoading } =
    useStocksServiceGetInternalInquiryEndpoint(
      {
        id: String(id)
      },
      [String(id)],
      {
        enabled: !!id
      }
    )

  const docs = Form.useWatch('docs', form)

  const onSave = async (isSend: boolean) => {
    try {
      const values = await form.validateFields()

      const items = values?.medicine?.map((item: MedicineProps) => ({
        inventoryItemId: item?.inventoryItemId,
        quantity: item?.quantity
      }))

      const shouldSaveFiles = docs?.length !== existingFiles.length

      let mediaItems = existingFiles

      if (shouldSaveFiles) {
        const uploadedFiles = await saveFiles()
        mediaItems = uploadedFiles
      }

      const payload = {
        contractId: values?.contractId,
        documentNumber: values?.documentNumber,
        documentDate: toDayjs(values?.documentDate)?.format(yearMonthDaySlash),
        comment: values?.comment,
        items,
        mediaItems
      }

      if (id) {
        await edit({ id: String(id), requestBody: payload })

        if (isSend) {
          await send({ id: String(id) })
        }
      } else mutate({ requestBody: payload })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  useEffect(() => {
    if (id && inquiryData) {
      const existingFileIds = inquiryData?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      setExistingFiles(existingFileIds)

      form.setFieldsValue({
        comment: inquiryData?.comment,
        medicine: inquiryData?.items?.map((item) => ({
          barCode: item?.barCode,
          name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
          producer: item?.producer?.name,
          expirationDate: toDayjs(item?.expirationDate),
          inventoryItemId: item?.inventoryItemId,
          form: {
            name: item?.productForm?.name,
            id: item?.productForm?.id
          },
          quantity: item?.quantity
        })),

        docs: existingFileIds.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [inquiryData, id])

  if (isLoading) {
    return <LoadingScreen />
  }

  const pageTitle = t(
    `stock_applications.${id ? 'edit_application' : 'create_new_application'}`
  )

  const isNew = inquiryData?.state === InternalInventoryDocumentState.NEW

  const isCreateOrNew = !inquiryData || (inquiryData && isNew)

  return (
    <PageBreadcrumbs
      title={pageTitle}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.applications') },
        {
          title: pageTitle
        }
      ]}
    >
      <Flex vertical gap={24} className={styles.form}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Space size={8}>
            {isNew && (
              <Button onClick={onBackClick}>{t('common.cancel')}</Button>
            )}

            {isCreateOrNew && (
              <SaveConfirm
                title={`${t('stock_applications.save_as_draft')}?`}
                placement="leftBottom"
                onConfirm={() => onSave(false)}
                okButtonProps={{ loading: isUploading || isLoading }}
              >
                <Button id="create-inquiry" form="create-inquiry">
                  {t('stock_applications.save_as_draft')}
                </Button>
              </SaveConfirm>
            )}
            {isNew && (
              <SaveConfirm
                title={`${t('stock_applications.send')}?`}
                placement="leftBottom"
                onConfirm={() => onSave(true)}
              >
                <Button type="primary">{t('stock_applications.send')}</Button>
              </SaveConfirm>
            )}
          </Space>
        </Flex>

        <Form
          layout="vertical"
          name="create-inquiry"
          form={form}
          onFinish={() => onSave(false)}
          disabled={!isCreateOrNew}
          scrollToFirstError
        >
          <Flex vertical gap={16}>
            <Card>
              <Form.Item name="medicine" rules={requiredRules}>
                <Form.List name="medicine">
                  {(fields, { remove }) => (
                    <LocalConsumableListCreate
                      fields={fields}
                      remove={remove}
                      form={form}
                      isFinished={!isCreateOrNew}
                    />
                  )}
                </Form.List>
              </Form.Item>
            </Card>

            <Card>
              <Flex gap={24} vertical>
                <Card title={t('stock_applications.uploaded_documents')}>
                  <Form.Item name="docs" className="full-width">
                    <div id="docs-wrapper">
                      <Upload
                        defaultFileList={
                          inquiryData?.mediaFiles?.map((file) => ({
                            ...file,
                            uid: file.id,
                            name: file.name
                          })) as UploadFile[]
                        }
                        onChange={onFileChange}
                        customRequest={() => null}
                        itemRender={(_, file, __, { remove }) => (
                          <CustomItemRender file={file} onRemove={remove} />
                        )}
                      />
                    </div>
                  </Form.Item>
                </Card>

                <Card title={t('common.comment')}>
                  <Form.Item name="comment">
                    <Input.TextArea
                      disabled={!!id}
                      rows={5}
                      placeholder={t('common.leave_comment')}
                    />
                  </Form.Item>
                </Card>
              </Flex>
            </Card>
          </Flex>
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
